import {
  AimOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FolderOutlined,
  FormOutlined,
  HomeOutlined,
  InboxOutlined,
  ProjectOutlined,
  QuestionCircleOutlined,
  SafetyOutlined,
  TeamOutlined,
  TruckOutlined,
  UsergroupAddOutlined
} from "@ant-design/icons"
import type {
  ItemType,
  MenuItemType,
  SubMenuType
} from "antd/es/menu/interface"
import React from "react"

import { type PermissionPages } from "../SettingsPermissions/types/permissions.types.ts"

interface SubMenu extends Omit<SubMenuType, "children"> {
  children: Array<ItemType>
}

export type SidebarItem = (MenuItemType | SubMenu) & {
  key: PermissionPages | string
}

interface SidebarConfig {
  main: SidebarItem[]
  bottom: SidebarItem[]
}

export const REPORTS_NAV_KEY = "reports"
export const INVOICES_NAV_KEY = "invoices"
export const ARCHIVE_NAV_KEY = "archive"
export const MAP_NAV_KEY = "map"
export const SIGNATURE_REQUESTS_NAV_KEY = "signature-requests"

// Marks the real nav row a Guide tour step spotlights (see the Product
// Tour's "nav-leads" step, tour/ProductTourContext.tsx) without changing how
// the row looks or behaves — `title` keeps the collapsed-sidebar tooltip
// that a plain string label gets for free, since wrapping `label` in a span
// loses it otherwise.
const navTarget = (id: string, text: string) => ({
  label: <span data-guide-target={id}>{text}</span>,
  title: text
})

// One outline set for the whole rail. The shared `Icon` registry mixes solid
// glyphs with stroked ones (and `Archive` hardcodes its own grey, so it could
// never take the selected row's colour), which read as an inconsistent set once
// the rows sit together in groups. These are AntD's own outline icons, scoped
// to the sidebar — the registry is left alone for the rest of the app.
//
// Every row's icon sits beside its own text label, so the icon is hidden from
// assistive tech rather than announcing its glyph name next to it.
export const sidebarConfig: SidebarConfig = {
  main: [
    {
      key: "home",
      icon: <HomeOutlined aria-hidden />,
      ...navTarget("nav-home", "Home")
    },
    {
      key: REPORTS_NAV_KEY,
      icon: <BarChartOutlined aria-hidden />,
      ...navTarget("nav-reports", "Reports")
    },
    {
      key: "leads",
      icon: <AimOutlined aria-hidden />,
      ...navTarget("nav-leads", "Leads")
    },
    {
      key: "projects",
      icon: <ProjectOutlined aria-hidden />,
      ...navTarget("nav-projects", "Projects")
    },
    {
      key: SIGNATURE_REQUESTS_NAV_KEY,
      icon: <FormOutlined aria-hidden />,
      label: "Signature Manager"
    },
    {
      key: ARCHIVE_NAV_KEY,
      icon: <InboxOutlined aria-hidden />,
      label: "Archive"
    },
    {
      key: "approval",
      icon: <CheckCircleOutlined aria-hidden />,
      label: "Approval",
      children: [
        {
          key: "proposals",
          label: "Proposal Review"
        },
        {
          key: "contracts",
          label: "Contract Review"
        },
        {
          key: "document_reviews",
          label: "Document Review"
        }
      ]
    },
    {
      key: INVOICES_NAV_KEY,
      icon: <DollarOutlined aria-hidden />,
      ...navTarget("nav-invoices", "Invoices")
    },
    {
      key: "calendar",
      icon: <CalendarOutlined aria-hidden />,
      ...navTarget("nav-calendar", "Calendar")
    },
    {
      key: "files",
      icon: <FolderOutlined aria-hidden />,
      ...navTarget("nav-files", "Files")
    },
    {
      key: MAP_NAV_KEY,
      icon: <EnvironmentOutlined aria-hidden />,
      label: "Location"
    },
    {
      key: "customers",
      icon: <TeamOutlined aria-hidden />,
      ...navTarget("nav-customers", "Customers")
    },
    {
      key: "subcontractors",
      icon: <SafetyOutlined aria-hidden />,
      ...navTarget("nav-subcontractors", "Subcontractors")
    },
    {
      key: "suppliers",
      icon: <TruckOutlined aria-hidden />,
      label: "Suppliers"
    },
    {
      key: "team_management",
      icon: <UsergroupAddOutlined aria-hidden />,
      ...navTarget("nav-team-management", "Team Management")
    }
  ],
  bottom: [
    {
      key: "guide",
      icon: <QuestionCircleOutlined aria-hidden />,
      label: "Guide"
    }
  ]
}

// Settings is not a rail row. It left the sidebar's bottom section for the
// header's utility group, and then that button left too: the header's profile
// menu (`containers/User/HeaderUserMenu`) is the only place it is offered now
// — same `/settings` route, same (ungated, as it always was) availability. The
// Company HQ product tour spotlights it through the target below, which the
// profile menu's trigger carries, since the menu is closed when the tour runs.
export const SETTINGS_NAV_KEY = "settings"
export const SETTINGS_GUIDE_TARGET = "nav-settings"

// The five visual groups the nav is read in. Purely presentational: a group
// never grants or hides a row — `SidebarBody` filters by permission first and
// only then sorts what survived into these buckets, so a group with nothing
// authorized in it simply does not render. Anything not named here still
// renders, ungrouped, below them rather than disappearing.
export const NAV_GROUPS: Array<{
  id: string
  labelKey: string
  keys: string[]
}> = [
  {
    id: "workspace",
    labelKey: "shell.nav.groups.workspace",
    keys: ["home", REPORTS_NAV_KEY]
  },
  {
    id: "operations",
    labelKey: "shell.nav.groups.operations",
    keys: ["leads", "projects", SIGNATURE_REQUESTS_NAV_KEY, ARCHIVE_NAV_KEY]
  },
  {
    id: "finance",
    labelKey: "shell.nav.groups.finance",
    keys: ["approval", INVOICES_NAV_KEY]
  },
  {
    id: "coordination",
    labelKey: "shell.nav.groups.coordination",
    keys: ["calendar", "files", MAP_NAV_KEY]
  },
  {
    id: "people",
    labelKey: "shell.nav.groups.people",
    keys: ["customers", "subcontractors", "suppliers", "team_management"]
  }
]

// Wraps the already-permission-filtered rows in AntD `type: "group"` items so
// the headings are part of the same Menu — one keyboard sequence, one set of
// `data-menu-id`s, collapsed-mode flyouts unchanged — rather than a stack of
// separate Menus.
export const groupNavItems = (
  items: SidebarItem[],
  t: (key: string) => string
): ItemType[] => {
  const byKey = new Map(items.map((item) => [String(item.key), item]))
  const grouped: ItemType[] = []

  NAV_GROUPS.forEach((group) => {
    const children = group.keys
      .map((key) => byKey.get(key))
      .filter((item): item is SidebarItem => Boolean(item))

    children.forEach((item) => byKey.delete(String(item.key)))
    if (!children.length) return

    grouped.push({
      key: `group-${group.id}`,
      type: "group",
      label: t(group.labelKey),
      children
    })
  })

  return [...grouped, ...byKey.values()]
}

// The Guide row reads as two lines, like the rest of the rail's bottom anchor.
// `title` keeps the plain-text tooltip the collapsed rail gets for free from a
// string label, which a node label would otherwise lose.
export const withGuideHint = (
  items: SidebarItem[],
  t: (key: string) => string
): SidebarItem[] =>
  items.map((item) =>
    item.key === "guide"
      ? {
          ...item,
          title: String(item.label),
          label: (
            <span className="sidebar-anchor-label">
              <span>{item.label}</span>
              <span className="sidebar-anchor-hint">{t("shell.nav.guideHint")}</span>
            </span>
          )
        }
      : item
  )
